import { X } from 'lucide-react'
import { useState } from 'react'

import { Field } from '@/components/ui/Field'
import { TagChip } from '@/components/ui/TagChip'
import { Button } from '@/components/ui/Button'
import { foldTag } from '@/lib/tags'

/**
 * Tags on a recipe: a list of chips with a free-text box underneath.
 *
 * The vocabulary belongs to the user, so this is not a closed list -- tags
 * already in use are offered as suggestions and anything can be typed. Values
 * are folded to `lower_snake_case` on the way in, both here and again on the
 * server, so `Instant Pot`, `instant pot` and `instant_pot` are one tag.
 */
export function TagEditor({
  tags,
  suggestions,
  onChange,
}: {
  tags: string[]
  suggestions: string[]
  onChange: (next: string[]) => void
}) {
  const [draft, setDraft] = useState('')
  const available = suggestions.filter(
    (tag) => tag && !tags.includes(tag) && tag.includes(draft.toLowerCase()),
  )

  function add(raw: string) {
    const tag = foldTag(raw)
    if (!tag || tags.includes(tag)) {
      setDraft('')
      return
    }
    onChange([...tags, tag])
    setDraft('')
  }

  function remove(tag: string) {
    onChange(tags.filter((entry) => entry !== tag))
  }

  return (
    <Field
      label="Tags"
      hint="Rules can match tags, so use words you'd search for."
    >
      {(id, describedBy) => (
        <div className="flex flex-col gap-2">
          {tags.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <li key={tag} className="flex items-center gap-0.5">
                  <TagChip tag={tag} />
                  <button
                    type="button"
                    onClick={() => remove(tag)}
                    aria-label={`Remove tag ${tag.replace(/_/g, ' ')}`}
                    className="rounded-full p-0.5 text-ink-faint hover:bg-surface-sunken hover:text-ink"
                  >
                    <X size={12} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="flex gap-2">
            <input
              id={id}
              type="text"
              aria-describedby={describedBy}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ',') {
                  event.preventDefault()
                  add(draft)
                }
              }}
              placeholder="Add a tag…"
              list={available.length > 0 ? 'tag-suggestions' : undefined}
              className="min-w-0 flex-1 rounded-[var(--radius-field)] border border-line bg-surface px-3 py-2 text-sm placeholder:text-ink-faint focus-visible:border-accent"
            />
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={() => add(draft)}
              disabled={!foldTag(draft)}
            >
              Add
            </Button>
          </div>

          {available.length > 0 ? (
            <datalist id="tag-suggestions">
              {available.map((tag) => (
                <option key={tag} value={tag} />
              ))}
            </datalist>
          ) : null}
        </div>
      )}
    </Field>
  )
}
